import { connect } from 'react-redux';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { Button } from '@iwoca/orion';

import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import { Tooltip } from '../../../ui/tooltip/Tooltip';
import { ChooseLoanAmount } from '../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';
import messages from './CustomiseOffer.messages';
import styles from './CustomiseOffer.module.css';
import { Dispatch } from 'redux';
import { FormalOffer } from '../../MyOffers.types';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import { GetFormalOffersResponse } from '@iwoca/lapi-client/edge';
import { MIN_DRAWDOWN_AMOUNT } from '../../../minDrawdownAmount';
import { LoanSummary } from './loanSummary/LoanSummary';
import { OfferChoiceButtons } from './offerChoiceButtons/OfferChoiceButtons';
import { getMaxAvailableCash, isInterestOnly } from '../../MyOffers.helpers';
import { SelectOfferType } from './selectOfferType/selectOfferType';
import { useState } from 'react';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: ({
        amount,
        offerId
    }: {
        amount: number;
        offerId: FormalOffer['offer_id'];
    }) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId,
                amount
            })
        });
    }
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;
type CustomiseOfferProps = PropsFromRedux & {
    drawdownAmount: number;
    setDrawdownAmount: (drawdownAmount: number) => void;
    selectedOffer: FormalOffer | null;
    updateSelectedOffer: (offer: FormalOffer) => void;
    formalOffers: NonNullable<GetFormalOffersResponse['data']>;
    showNewUI: boolean;
};

type FormalOffers = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'];

const CustomiseOffer = ({
    startDrawdown,
    drawdownAmount,
    setDrawdownAmount,
    selectedOffer,
    updateSelectedOffer,
    formalOffers,
    showNewUI
}: CustomiseOfferProps) => {
    const maxAvailableCash = getMaxAvailableCash(formalOffers!.formal_offers);

    //set default initial Offers when there are only 2 formal offers
    const initalOfferChoices =
        formalOffers.formal_offers.length === 2
            ? formalOffers.formal_offers
            : [];

    const [offerChoices, setOfferChoices] = useState<FormalOffers | []>(
        initalOfferChoices
    );

    if (formalOffers.formal_offers.length === 1) {
        //default the selected offer to the first offer since user has no choice
        updateSelectedOffer(formalOffers!.formal_offers[0]);
    }

    //needed to indicate the copy that is used in section headings
    const hasInterestOnlyChoice = Boolean(
        formalOffers!.formal_offers.find((formal_offer) =>
            isInterestOnly(formal_offer)
        )
    );

    return (
        <>
            <ChooseLoanAmount
                setDrawdownAmount={setDrawdownAmount}
                drawdownAmount={drawdownAmount}
                minimumDrawdownAmount={MIN_DRAWDOWN_AMOUNT}
                maximumDrawdownAmount={maxAvailableCash}
                inputTooltip={
                    <Tooltip
                        data-action-name="Input tooltip"
                        message={
                            <FormattedMessage
                                {...messages.inputTooltip}
                                values={{
                                    amount: (
                                        <MoneyNoDecimals
                                            value={maxAvailableCash}
                                        />
                                    ),
                                    date: (
                                        <FormattedDate
                                            value={
                                                selectedOffer
                                                    ? new Date(
                                                          selectedOffer.timeout!
                                                      ).toISOString()
                                                    : new Date()
                                            }
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                        />
                                    )
                                }}
                            />
                        }
                    />
                }
            />

            {formalOffers!.formal_offers.length > 2 && (
                <SelectOfferType
                    formalOffers={formalOffers!.formal_offers}
                    setOfferChoices={setOfferChoices}
                    drawdownAmount={drawdownAmount}
                />
            )}

            {offerChoices.length > 0 && (
                <OfferChoiceButtons
                    updateSelectedOffer={updateSelectedOffer}
                    formalOffers={offerChoices}
                    drawdownAmount={drawdownAmount}
                    hasInterestOnlyChoice={hasInterestOnlyChoice}
                />
            )}

            {!showNewUI && (
                <>
                    <div className={styles.ctaContainer}>
                        <Button
                            variant="success"
                            data-testid="start-drawdown"
                            data-action-name="Approval continue button"
                            disabled={Boolean(!selectedOffer)}
                            onClick={() =>
                                startDrawdown({
                                    amount: drawdownAmount,
                                    offerId: selectedOffer!.offer_id
                                })
                            }
                            className={styles.cta}
                        >
                            <FormattedMessage
                                {...messages.cta}
                                values={{
                                    amount: (
                                        <MoneyNoDecimals
                                            className={styles.summaryAmount}
                                            value={drawdownAmount}
                                        />
                                    )
                                }}
                            />
                        </Button>
                        <div className={styles.ctaHeplerText}>
                            {selectedOffer && (
                                <FormattedMessage {...messages.CTAinfo} />
                            )}
                        </div>
                    </div>
                    <LoanSummary
                        drawdownAmount={drawdownAmount}
                        selectedOffer={selectedOffer}
                        showNewUI={showNewUI}
                    />
                </>
            )}
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(CustomiseOffer);
